import { LayoutDashboard } from "lucide-react-native";
import { memo } from "react";
import { Platform } from "react-native";

import HeaderActivityPopover from "@/components/organisms/HeaderActivityPopover";
import HeaderCreateMenu from "@/components/organisms/HeaderCreateMenu";
import HeaderSearchButton from "@/components/organisms/HeaderSearchButton";

import HeaderPopover from "@/components/organisms/HeaderPopover";
import { LogoIcon } from "@/components/molecules/icons";
// design-system: button size snapped xl→lg (closed size scale)
import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/**
 * Props for CustomHeaderView component
 */
interface Props {
  firstName: string | null;
  isSmallScreen: boolean;
  onLogoPress?: () => void;
  onKanbanToggle?: () => void;
  isDark: boolean;
  showNavigationIcons: boolean;
}

/**
 * Custom app header — logo, navigation icons, and safe-area top inset.
 * @param root0 - Component props
 * @param root0.firstName - User first name
 * @param root0.isSmallScreen - Whether the screen is mobile sized
 * @param root0.onLogoPress - Logo press handler
 * @param root0.onKanbanToggle - Kanban toggle handler
 * @param root0.isDark - Whether dark theme is active
 * @param root0.showNavigationIcons - Whether to show navigation icons
 */
const CustomHeaderView = ({
  firstName,
  isSmallScreen,
  onLogoPress,
  onKanbanToggle,
  isDark,
  showNavigationIcons,
}: Props) => (
  <Box
    className={`bg-surface-base ${!isSmallScreen ? `${isDark ? "" : "border-b border-surface-muted"}` : "pb-6"}`}
  >
    {!isSmallScreen ? (
      <Stack
        direction="horizontal"
        className="items-center justify-between gap-10 px-4 py-2"
      >
        <Stack direction="horizontal" className="items-center gap-5 py-2">
          <Pressable onPress={onLogoPress}>
            <Icon as={LogoIcon} size="xl" className="" />
          </Pressable>
          <Text
            variant="display-sm"
            className={isDark ? "text-white" : "text-content-primary"}
          >
            Hey{firstName ? `, ${firstName}` : " there"}
          </Text>
        </Stack>
        <Stack direction="horizontal" className="gap-3">
          {!isSmallScreen && Platform.OS === "web" && <HeaderSearchButton />}
          {showNavigationIcons && Platform.OS === "web" && (
            <Button
              size="lg"
              variant="link"
              onPress={onKanbanToggle}
              className="rounded-none px-3 py-2"
              accessible
              accessibilityLabel="Toggle kanban view"
              accessibilityRole="button"
              icon={LayoutDashboard}
            />
          )}
          <HeaderCreateMenu />
          {!isSmallScreen && <HeaderActivityPopover />}
          <HeaderPopover />
        </Stack>
      </Stack>
    ) : (
      <Stack className="gap-10">
        <Stack
          direction="horizontal"
          className="items-center justify-between pb-4"
        >
          <Pressable onPress={onLogoPress}>
            <Icon as={LogoIcon} size="xl" className="" />
          </Pressable>
          <HeaderPopover />
        </Stack>
        <Text
          variant="display-lg"
          className={`pb-3 ${isDark ? "text-white" : "text-content-primary"}`}
        >
          Hey{firstName ? `, ${firstName}` : " there"}
        </Text>
      </Stack>
    )}
  </Box>
);

CustomHeaderView.displayName = "CustomHeaderView";

export default memo(CustomHeaderView);
